/**
 * VelocityView Component
 *
 * Displays product velocity data as a custom list with progress bars.
 * Part of the Analytics Card split for better maintainability.
 *
 * Features:
 * - Ranked list of products by velocity
 * - Progress bars with purple gradient (darker = faster)
 * - Expand/collapse for products list
 * - Smooth animation on expand
 *
 * @see TECHNICAL_DEBT_ROADMAP.md Phase 2.2
 */

import {
  BlockStack,
  InlineStack,
  Text,
  Box,
  Button,
  Thumbnail,
} from '@shopify/polaris';
import { ChartVerticalIcon, PackageIcon, ChevronDownIcon, ChevronUpIcon } from '@shopify/polaris-icons';
import { useState } from 'react';
import type { VelocityAnalyticsItem } from '../../../types/analytics';
import { TabEmptyState } from '../TabEmptyState';

/**
 * Brand purple shades for progress bars (darker = higher velocity)
 */
const PURPLE_SHADES = [
  '#6b5bb8', // darkest - highest velocity
  '#7a6ac5',
  '#8b7bd8', // brand purple
  '#9d8de5',
  '#ae9eef',
  '#bfb0f7', // lightest - lowest velocity
];

/**
 * Number of items to show when collapsed
 */
const COLLAPSED_ITEM_COUNT = 5;

/**
 * Props for VelocityView
 */
interface VelocityViewProps {
  /** Velocity analytics data */
  data: VelocityAnalyticsItem[];
}

/**
 * Get purple shade based on velocity rank (darker = faster)
 */
function getPurpleShade(index: number, total: number): string {
  const shadeIndex = Math.min(Math.floor((index / total) * PURPLE_SHADES.length), PURPLE_SHADES.length - 1);
  return PURPLE_SHADES[shadeIndex];
}

/**
 * Velocity View
 * Displays a ranked list of products with velocity progress bars
 */
export function VelocityView({ data }: VelocityViewProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasData = data && data.length > 0;
  if (!hasData) {
    return (
      <div className="empty-state-section">
        <div className="analytics-content-enter">
          <TabEmptyState
            icon={ChartVerticalIcon}
            title="No velocity data available"
            description="Velocity data is calculated from sales history"
          />
        </div>
      </div>
    );
  }

  // Find max velocity for progress bar scaling
  const maxVelocity = Math.max(...data.map(item => item.averageVelocity || 0), 1);

  // Show limited items when collapsed, all when expanded
  const shouldShowExpand = !isExpanded && data.length > COLLAPSED_ITEM_COUNT;
  // Only render visible items when collapsed to avoid rendering issues
  const displayedItems = isExpanded ? data : data.slice(0, COLLAPSED_ITEM_COUNT);

  return (
    <div className="velocity-view-wrapper" style={{ height: 'auto' }}>
      <div className="analytics-content-enter" style={{ height: 'auto' }}>
        <div
          className="velocity-chart-container"
          style={{
            position: 'relative',
          }}
        >
          <BlockStack gap="300">
            {displayedItems.map((item, index) => {
              const velocity = item.averageVelocity || 0;
              const progressPercent = (velocity / maxVelocity) * 100;
              const purpleShade = getPurpleShade(index, displayedItems.length);

              // Animate items beyond collapsed count when expanding
              const shouldAnimate = isExpanded && index >= COLLAPSED_ITEM_COUNT;
              const animationDelay = shouldAnimate ? (index - COLLAPSED_ITEM_COUNT) * 0.05 : 0;

              // Apply fade effect to last item when collapsed (indicating more items below)
              const isLastCollapsedItem = !isExpanded && index === COLLAPSED_ITEM_COUNT - 1 && data.length > COLLAPSED_ITEM_COUNT;

              return (
                <Box
                  key={item.variantId || index}
                  padding="200"
                  background="bg-surface-secondary"
                  borderRadius="200"
                >
                  <div
                    className="velocity-product-card"
                    style={{
                      opacity: shouldAnimate ? 0 : isLastCollapsedItem ? 0.4 : 1,
                      transform: shouldAnimate ? 'translateY(-10px)' : 'none',
                      animation: shouldAnimate
                        ? `velocityItemPopIn 0.3s ease-out ${animationDelay}s forwards`
                        : undefined,
                      animationDelay: !shouldAnimate ? `${index * 50}ms` : undefined,
                      transition: 'opacity 0.2s ease',
                    }}
                  >
                    <InlineStack gap="400" blockAlign="center" wrap={false}>
                      {/* Product thumbnail */}
                      <Thumbnail
                        source={item.imageUrl || PackageIcon}
                        alt={item.productTitle}
                        size="small"
                      />

                      {/* Product info and velocity bar */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <BlockStack gap="100">
                          <InlineStack align="space-between" blockAlign="center">
                            <BlockStack gap="050">
                              <Text as="span" variant="bodyMd" fontWeight="medium">
                                {item.productTitle}
                              </Text>
                              {item.variantTitle && (
                                <Text as="span" variant="bodySm" tone="subdued">
                                  {item.variantTitle}
                                </Text>
                              )}
                            </BlockStack>

                            {/* Velocity score - always visible */}
                            <Box minWidth="80px">
                              <BlockStack gap="050" align="end">
                                <span className="velocity-stat">
                                  <Text as="span" variant="headingSm" fontWeight="bold">
                                    {velocity.toFixed(1)}
                                  </Text>
                                </span>
                                <Text as="span" variant="bodySm" tone="subdued">
                                  units/day
                                </Text>
                              </BlockStack>
                            </Box>
                          </InlineStack>

                          {/* Custom purple progress bar */}
                          <Box paddingBlockStart="100">
                            <div
                              style={{
                                height: '6px',
                                width: '100%',
                                backgroundColor: '#e4e5e7',
                                borderRadius: '3px',
                                overflow: 'hidden',
                              }}
                            >
                              <div
                                className="velocity-bar"
                                style={{
                                  height: '100%',
                                  width: `${progressPercent}%`,
                                  backgroundColor: purpleShade,
                                  borderRadius: '3px',
                                  animationDelay: `${100 + index * 50}ms`,
                                }}
                              />
                            </div>
                          </Box>
                        </BlockStack>
                      </div>
                    </InlineStack>
                  </div>
                </Box>
              );
            })}
          </BlockStack>
        </div>
      </div>

      {/* Show more button */}
      {shouldShowExpand && (
        <Box paddingBlockStart="300">
          <Button
            onClick={() => setIsExpanded(true)}
            fullWidth
            variant="secondary"
            icon={ChevronDownIcon}
          >
            {`Show all ${data.length} products`}
          </Button>
        </Box>
      )}

      {/* Show less button when expanded */}
      {isExpanded && data.length > COLLAPSED_ITEM_COUNT && (
        <Box paddingBlockStart="300">
          <Button
            onClick={() => setIsExpanded(false)}
            fullWidth
            variant="secondary"
            icon={ChevronUpIcon}
          >
            Show less
          </Button>
        </Box>
      )}
    </div>
  );
}
